import { AccessUser } from 'domain/access-user';
import { urlBaseApi } from 'global';
import { RequestMethod } from 'shared/domain/http/request-method';
import { setActiveBranchOfficeId } from 'shared/utils/active-branch-office';
import { AppError } from 'shared/utils/app-error';
import { DateFormat, format } from 'shared/utils/date';
import { getRequestOptions } from 'shared/utils/request-options';
import { saveSessionToken } from 'shared/utils/session-token';
import { Login } from '../../../domain/login';
import { LoginError, LoginErrorResponse } from '../../../domain/login-error';
import { fetch } from './autentication-interceptor';
import { loginMapper } from './mapper/login.mapper';

const login = async (dni: string, password: string): Promise<Login> => {
  const response = await window.fetch(`${urlBaseApi}/login`, {
    method: RequestMethod.Post,
    ...getRequestOptions(false),
    body: JSON.stringify({ dni, password }),
  });
  const result = await response.json();
  if (!response.ok) {
    throw new LoginError(response.status, result as LoginErrorResponse);
  }

  const { data, token } = result;
  if (token) {
    saveSessionToken(token);
  }
  setActiveBranchOfficeId(null);
  return loginMapper(data, token);
};

const logout = async (): Promise<{ message: string }> => {
  const url = `${urlBaseApi}/logout`;
  const response = await fetch(url, {
    method: RequestMethod.Get,
    ...getRequestOptions(),
  });
  setActiveBranchOfficeId(null);
  const result = await response.json();
  if (response.ok) {
    return result;
  }

  throw new AppError({ ...result, status: response.status });
};

const startImpersonation = async (userId: number): Promise<{ login: Login; impersonationLogId: number }> => {
  const url = `${urlBaseApi}/users/${userId}/impersonate`;
  const response = await fetch(url, {
    method: RequestMethod.Post,
    ...getRequestOptions(),
  });
  const result = await response.json();
  if (response.ok) {
    const { data, token, impersonation_log_id: impersonationLogId } = result;
    return { login: loginMapper(data, token), impersonationLogId };
  }

  throw new AppError({ ...result, status: response.status });
};

const stopImpersonation = async (impersonationLogId: number): Promise<void> => {
  const url = `${urlBaseApi}/impersonate/stop`;
  const response = await fetch(url, {
    method: RequestMethod.Post,
    ...getRequestOptions(),
    body: JSON.stringify({ impersonation_log_id: impersonationLogId }),
  });
  const result = await response.json();
  if (response.ok) return;

  throw new AppError({ ...result, status: response.status });
};

const getAccessModuleUser = async (): Promise<AccessUser> => {
  const url = `${urlBaseApi}/access/me`;
  const response = await fetch(url, {
    method: RequestMethod.Get,
    ...getRequestOptions(),
  });
  const result = await response.json();
  if (response.ok) {
    const { user, modules } = result;
    return {
      user: {
        id: user.id,
        firstName: user.first_name,
        email: user.email,
        dni: user.dni,
        lastName: user.last_name,
        birthdate: user.birthdate ? format(user.birthdate, DateFormat.ShortMonthWithYear) : '',
      },
      modules,
    };
  }

  throw new AppError({ ...result, status: response.status });
};

export const RequestAutentication = {
  login,
  logout,
  getAccessModuleUser,
  startImpersonation,
  stopImpersonation,
};
